import { Button, Form, Input, InputNumberProps, message, Select, Slider, Typography } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from '../../../i18n/i18n';
import './CreateInstallation.scss';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import ModalCustom from '../../utils/ModalCustom/ModalCustom';
import { SiphonDiameter, SiphonMaterial, SiphonType } from '../../../gen/proto/api/gateway/v1/installation_pb';
import { installationApi } from '../../../api/installation.api';
import { siphonDiameterLabel, siphonMaterialLabel, siphonTypeLabel } from '../../../utils/mapTranslationEnums';

const { Text } = Typography;

type InstallationValues = {
  name: string;
  erpId: string;
  type: SiphonType;
  material: SiphonMaterial;
  diameter: SiphonDiameter;
  effluent: string;
  limit: number;
};

type CreateInstallationProps = {
  uuid?: string;
  locationUuid?: string;
  onSuccess?: () => void;
};

const CreateInstallation = ({ uuid, locationUuid, onSuccess }: CreateInstallationProps) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [resolvedLocationUuid, setResolvedLocationUuid] = useState<string>('');
  const [resolvedComment, setResolvedComment] = useState<string>('');
  const { t } = useTranslation('installation-location', { keyPrefix: 'installationLocationDetail.modal' });
  const [form] = Form.useForm<InstallationValues>();
  const siphonType = Form.useWatch('type', form);

  const siphonTypeOptions = [
    { value: SiphonType.EFFLUENT, label: siphonTypeLabel(SiphonType.EFFLUENT) },
    { value: SiphonType.INFLUENT, label: siphonTypeLabel(SiphonType.INFLUENT) },
  ];

  const siphonMaterialOptions = [
    { value: SiphonMaterial.PVC, label: siphonMaterialLabel(SiphonMaterial.PVC) },
    { value: SiphonMaterial.HDPE, label: siphonMaterialLabel(SiphonMaterial.HDPE) },
    { value: SiphonMaterial.OTHER, label: siphonMaterialLabel(SiphonMaterial.OTHER) },
  ];

  const siphonDiameterOptions = [
    { value: SiphonDiameter.SIPHON_DIAMETER_90_MM, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_90_MM) },
    { value: SiphonDiameter.SIPHON_DIAMETER_110_MM, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_110_MM) },
    { value: SiphonDiameter.SIPHON_DIAMETER_160_MM, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_160_MM) },
    { value: SiphonDiameter.SIPHON_DIAMETER_200_MM, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_200_MM) },
    { value: SiphonDiameter.SIPHON_DIAMETER_240_MM, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_240_MM) },
    { value: SiphonDiameter.SIPHON_DIAMETER_OTHER, label: siphonDiameterLabel(SiphonDiameter.SIPHON_DIAMETER_OTHER) },
  ];

  const onChange: InputNumberProps['onChange'] = (newValue) => {
    setInputValue(newValue as number);
  };

  const handleOpen = async () => {
    if (uuid) {
      try {
        const installation = await installationApi.getByUuid(uuid);
        const limit = installation.siphonCollapseLimitPercentage ?? 0;
        form.setFieldsValue({
          name: installation.name,
          erpId: installation.erpId,
          limit,
          type: installation.siphonType,
          material: installation.siphonMaterial,
          diameter: installation.siphonDiameterMm,
          effluent: installation.siphonEffluentSiphonUuid,
        });
        setInputValue(limit);
        setResolvedLocationUuid(installation.locationUuid);
        setResolvedComment(installation.comment ?? '');
      } catch (e) {
        messageApi.error(t('common:errors.generic'));
      }
    } else {
      form.resetFields();
      setInputValue(0);
      setResolvedLocationUuid(locationUuid ?? '');
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (uuid) {
        await installationApi.update(
          uuid,
          values.name,
          values.erpId,
          resolvedLocationUuid,
          values.type,
          values.material,
          values.diameter,
          values.effluent || undefined,
          inputValue,
          undefined,
          resolvedComment,
        );
      } else {
        await installationApi.create(
          values.name,
          values.erpId,
          values.type,
          values.material,
          values.diameter,
          values.effluent || undefined,
          inputValue,
          resolvedLocationUuid,
        );
      }
      setIsModalOpen(false);
      onSuccess?.();
    } catch (e) {
      messageApi.error(t('common:errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      {!uuid && (
        <Button type={'primary'} onClick={handleOpen}>
          <PlusOutlined />
          {t('create')}
        </Button>
      )}
      {uuid && (
        <Button type={'text'} size={'middle'} onClick={handleOpen}>
          <EditOutlined className={'InstallationDetailModal__edit__button'} />
        </Button>
      )}
      <ModalCustom
        title={uuid ? t('title_edit') : t('title_create')}
        description={t('description')}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        footer={
          <Button onClick={handleSubmit} loading={loading}>
            {uuid ? t('edit') : t('create')}
          </Button>
        }
      >
        <Form form={form} layout="vertical" className={'Modal__form'}>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="erpId" label={t('erpId')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Text className={'Modal__text'}>{t('siphonData')}</Text>
          <Form.Item name="type" label={t('siphonType')} rules={[{ required: true }]}>
            <Select options={siphonTypeOptions} />
          </Form.Item>
          <Form.Item name="material" label={t('material')} rules={[{ required: true }]}>
            <Select options={siphonMaterialOptions} />
          </Form.Item>
          <Form.Item name="diameter" label={t('diameter')} rules={[{ required: true }]}>
            <Select options={siphonDiameterOptions} />
          </Form.Item>
          {siphonType === SiphonType.INFLUENT && (
            <Form.Item name="effluent" label={t('effluent')} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          <Form.Item name="limit" label={t('siphonLimit')}>
            <div className={'Modal__slider'}>
              <Slider className={'Modal__slider__bar'} value={inputValue} onChange={onChange} />
              <Text className={'Modal__text'}>{inputValue}%</Text>
            </div>
          </Form.Item>
        </Form>
      </ModalCustom>
    </>
  );
};

export default CreateInstallation;
